import { AxiosError } from "axios";
import { 
    ExamSchedule, 
    Question, 
    ExamScheduleFilters,
    CreateExamScheduleRequest,
    UpdateExamScheduleRequest,
    ExamAnalytics,
    ExamAttemptInfo,
    DefaultExamFormat,
    ExamSchedulePublicDTO,
    SeatAvailabilityResponse,
} from "@/types/examination.types";
import {
    BulkUserImportRequestDTO,
    BulkUserImportResultDTO,
    BulkImportFileJobResponse,
    BulkImportJobStatusDTO,
} from "@/types/user.type";
import { Language } from "@/types/language.types";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export const useGetExamSchedules = (filters?: ExamScheduleFilters) => {
    return useQuery<ApiResponse<ExamSchedule[]>, AxiosError>({
        queryKey: ["exam-schedules", filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.append(key, value.toString());
                    }
                });
            }
            const response = await axios.get<ApiResponse<ExamSchedule[]>>(`/exam-schedules?${params}`);
            return response.data;
        },
    });
};

export const useGetExamSchedulesByCouncil = (councilId?: string, filters?: ExamScheduleFilters) => {
    return useQuery<ApiResponse<ExamSchedule[]>, AxiosError>({
        queryKey: ["exam-schedules", councilId, filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.append(key, value.toString());
                    }
                });
            }
            const response = await axios.get<ApiResponse<ExamSchedule[]>>(`/exam-schedules/council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetExamScheduleById = (id: string) => {
    return useQuery<ApiResponse<ExamSchedule>, AxiosError>({
        queryKey: ["exam-schedules", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamSchedule>>(`/exam-schedules/${id}`);
            return response.data;
        },
        refetchOnWindowFocus: false,
        enabled: !!id,
    });
};

export const useGetDefaultExamFormat = (educationProgramId: string) => {
    return useQuery<ApiResponse<DefaultExamFormat>, AxiosError>({
        queryKey: ["exam-schedules", "default-format", educationProgramId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<DefaultExamFormat>>(`/exam-schedules/default-format/${educationProgramId}`);
            return response.data;
        },
        enabled: !!educationProgramId,
    });
};

export const useGetExamSchedulesByStartDate = (startDate: string) => {
    return useQuery<ApiResponse<ExamSchedule[]>, AxiosError>({
        queryKey: ["exam-schedules", "start-date", startDate],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamSchedule[]>>(`/exam-schedules/start-date?startDate=${startDate}`);
            return response.data;
        },
        enabled: !!startDate,
    });
};

export const useCreateExamSchedule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<ExamSchedule>, AxiosError<{ message: string }>, CreateExamScheduleRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<ExamSchedule>>(`/exam-schedules`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["exam-schedules"] });
        },
    });
};

export const useUpdateExamSchedule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<ExamSchedule>, AxiosError<{ message: string }>, UpdateExamScheduleRequest>({
        mutationFn: async (data) => {
            const response = await axios.put<ApiResponse<ExamSchedule>>(`/exam-schedules/${data.id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["exam-schedules"] });
        },
    });
};

export const useDeleteExamSchedule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<ExamSchedule>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<ExamSchedule>>(`/exam-schedules/${id}`);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all exam-schedules queries
            queryClient.invalidateQueries({ queryKey: ["exam-schedules"] });
        },
    });
};

export const useGetRandomQuestionsByCouncil = (councilId: string, limit: number, enabled = true) => {
    return useQuery<ApiResponse<Question[]>, AxiosError>({
        queryKey: ["questions", "random", councilId, limit],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<Question[]>>(`/questions/council/${councilId}/random?limit=${limit}`);
            return response.data;
        },
        enabled: enabled && !!councilId && !!limit,
    });
};

export const useGetPublishedExamSchedulesByCouncil = (councilId?: string) => {
    return useQuery<ApiResponse<ExamSchedule[]>, AxiosError>({
        queryKey: ["exam-schedules", "published", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamSchedule[]>>(`/exam-schedules/council/${councilId}/published`);
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetPublishedUpcomingExamSchedulesByCouncil = (
    councilId?: string, 
    filters?: { startDate?: string; endDate?: string; location?: string }
) => {
    return useQuery<ApiResponse<ExamSchedulePublicDTO[]>, AxiosError>({
        queryKey: ["exam-schedules", "published-upcoming", councilId, filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.append(key, value.toString());
                    }
                });
            }
            const response = await axios.get<ApiResponse<ExamSchedulePublicDTO[]>>(`/exam-schedules/council/${councilId}/published?${params}`);
            return response.data;
        },
        enabled: !!councilId,
        staleTime: 0, // Always consider data stale to allow immediate refetch
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
};

// Exam Analytics
export const useGetExamAnalytics = (examScheduleId: string, enabled = true) => {
    return useQuery<ApiResponse<ExamAnalytics>, AxiosError>({
        queryKey: ["exam-analytics", examScheduleId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamAnalytics>>(`/exam-schedules/${examScheduleId}/analytics`);
            return response.data;
        },
        enabled: enabled && !!examScheduleId,
    });
};

// Seat availability for exam schedules
export const useGetExamScheduleSeatAvailability = (
    examScheduleId?: string,
    date?: string
) => {
    return useQuery<ApiResponse<SeatAvailabilityResponse>, AxiosError>({
        queryKey: ["exam-schedule-seat-availability", examScheduleId, date],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (date) {
                params.append("date", date);
            }
            const query = params.toString();
            const response = await axios.get<ApiResponse<SeatAvailabilityResponse>>(
                `/exam-schedules/${examScheduleId}/seat-availability${query ? `?${query}` : ""}`
            );
            return response.data;
        },
        enabled: !!examScheduleId,
        staleTime: 60_000,
    });
};

// Export Exam Analytics
export const useExportExamAnalytics = () => {
    return useMutation<void, AxiosError<{ message: string }>, { examScheduleId: string; format: 'excel' | 'csv' }>({
        mutationFn: async ({ examScheduleId, format }) => {
            const response = await axios.get(`/exam-schedules/${examScheduleId}/analytics/export?format=${format}`, {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `exam-analytics-${examScheduleId}.${format === 'excel' ? 'xlsx' : 'csv'}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        },
    });
};

// Legacy downloadExamAnalytics function for backward compatibility
export const downloadExamAnalytics = async (examScheduleId: string, format: 'excel' | 'csv') => {
    try {
        const response = await axios.get(`/exam-schedules/${examScheduleId}/analytics/export?format=${format}`, {
            responseType: 'blob',
        });
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        const fileExtension = format === 'excel' ? 'xlsx' : 'csv';
        let filename = `exam-analytics-${examScheduleId}.${fileExtension}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, filename };
    } catch (error) {
        console.error('Error downloading exam analytics:', error);
        throw error;
    }
};

// Exam Attempt Information
export const useGetExamAttemptInfo = (userId: string, councilId: string, enabled = true) => {
    return useQuery<ApiResponse<ExamAttemptInfo>, AxiosError>({
        queryKey: ["exam-attempt-info", userId, councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamAttemptInfo>>(
                `/exam-sessions/user/${userId}/attempt-info?councilId=${councilId}`
            );
            return response.data;
        },
        enabled: enabled && !!userId && !!councilId,
    });
};

// Bulk User Import
export const useBulkImportUsers = (councilId?: string) => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<BulkUserImportResultDTO>, AxiosError<{ message: string }>, BulkUserImportRequestDTO>({
        mutationFn: async (payload) => {
            const url = `/exam-schedules/council/${councilId}/bulk-import-users`;
            const response = await axios.post<ApiResponse<BulkUserImportResultDTO>>(url, payload);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate related caches
            queryClient.invalidateQueries({ queryKey: ["exam-schedules"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        }
    });
};

export const useBulkImportUsersFile = (councilId?: string) => {
    return useMutation<ApiResponse<BulkImportFileJobResponse>, AxiosError<{ message: string }>, File>({
        mutationFn: async (file) => {
            const form = new FormData();
            form.append("file", file);
            const response = await axios.post<ApiResponse<BulkImportFileJobResponse>>(
                `/exam-schedules/council/${councilId}/bulk-import-users/file`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            return response.data;
        },
    });
};

export const useGetBulkImportJobStatus = (councilId?: string, executionId?: number) => {
    return useQuery<ApiResponse<BulkImportJobStatusDTO>, AxiosError<{ message: string }>>({
        queryKey: ["bulk-import-job-status", councilId, executionId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<BulkImportJobStatusDTO>>(
                `/exam-schedules/council/${councilId}/bulk-import-users/file/status/${executionId}`
            );
            return response.data;
        },
        enabled: false,
    });
};

// Publish/Unpublish Exam Schedule
export const usePublishExamSchedule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<ExamSchedule>, AxiosError<{ message: string }>, { 
        id: string; 
        publish: boolean 
    }>({
        mutationFn: async ({ id, publish }) => {
            const response = await axios.put<ApiResponse<ExamSchedule>>(`/exam-schedules/${id}/publish?publish=${publish}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["exam-schedules"] });
        },
    });
};

// Get supported languages for an exam schedule
export const useGetExamScheduleSupportedLanguages = (examScheduleId?: string, enabled = true) => {
    return useQuery<ApiResponse<Language[]>, AxiosError>({
        queryKey: ["exam-schedules", examScheduleId, "supported-languages"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<Language[]>>(`/exam-schedules/${examScheduleId}/supported-languages`);
            return response.data;
        },
        enabled: enabled && !!examScheduleId,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });
};